import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import classes from "./Point.module.scss";

import Header from "../../molecules/header/Header";
import BreadCrumb from "../../molecules/breadcrumb/BreadCrumb";
import PointHeader from "../../organism/point-header/PointHeader";
import TopHeader from "../../organism/top-header/TopHeader";
import TeacherComment from "../../molecules/teacher-commnet/TeacherComment";
import rabbitNormal from "../../static/images/rabbit-normal.png";
import SimpleButton from "../../atoms/simple-button/SimpleButton";

import mokomokoGreen from "../../static/images/mokomoko-green.png";

import { fetchUser } from "../../api/UsersApi";
import { fetchUserPointHistory, UserPointHistory } from "../../api/UserPointHistoryApi";
import { fetchUserPointOwners, UserPointOwner } from "../../api/UserPointOwnerApi";
import line from "../../static/images/dashed-line.png";

const SparkleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
    <path d="M9.82808 4.69101L8.45272 4.35393C6.90545 3.96067 5.67335 2.69663 5.38682 1.15169L5.18625 0.168539C5.18625 0.168539 5.15759 0 4.98567 0C4.81375 0 4.7851 0.168539 4.7851 0.168539L4.58453 1.17978C4.26934 2.72472 3.0659 3.98876 1.51863 4.38202L0.143266 4.7191C0.143266 4.7191 0 4.74719 0 4.91573C0 5.08427 0.143266 5.11236 0.143266 5.11236L1.51863 5.44944C3.0659 5.8427 4.26934 7.10674 4.58453 8.67978L4.81376 9.85955C4.81376 9.85955 4.84241 10 5.01433 10C5.18625 10 5.2149 9.85955 5.2149 9.85955L5.44413 8.67978C5.73066 7.13483 6.9341 5.87079 8.51003 5.44944L9.85673 5.11236C9.85673 5.11236 10 5.08427 10 4.91573C10 4.74719 9.85673 4.7191 9.85673 4.7191L9.82808 4.69101Z" fill="white" />
  </svg>
);

const Point = () => {
  const userApi = fetchUser();
  const userPointHistoryApi = fetchUserPointHistory();
  const userPointOwnersApi = fetchUserPointOwners();
  const navigate     = useNavigate();
  const currentPoint = userApi.state?.data?.[0]?.currentPoint ?? 0;
  const pointOwners = userPointOwnersApi.state?.data ?? [];
  const pointSummaryOwners: UserPointOwner[] = pointOwners.length > 0
    ? pointOwners
    : [{
        pointOwnerId: 0,
        userId: "",
        ownerType: "user",
        ownerName: "",
        ownerBirthday: null,
        iconType: null,
        displayOrder: 0,
        currentPoint,
        availableGiftPoint: currentPoint,
        supportedPoint: 0,
        increasedPoint: 0,
        totalPoint: currentPoint,
        createdAt: "",
        updatedAt: null,
      }];

  useEffect(() => {
    userApi.fetch();
    userPointHistoryApi.fetch();
    userPointOwnersApi.fetch();

  }, []);

  const getOwnerLabel = (owner: UserPointOwner) => {
    return owner.ownerType === "user" ? "あなた" : owner.ownerName;
  };

  const getExchangeHistories = (owner: UserPointOwner) => {
    return userPointHistoryApi.state?.data?.filter(
      (history) => history.pointOwnerId === owner.pointOwnerId
    ) ?? [];
  };

  const renderExchangeHistory = (histories: UserPointHistory[]) => (
    <div className={classes.historyBody}>
      <div className={classes.historyTitle}>
        <div className={classes.titleRow}>
          <img src={mokomokoGreen} alt="もこもこ" />
          <span>ポイント交換履歴</span>
        </div>
        <img className={classes.lineImage} src={line} alt="ライン" />
      </div>

      {histories.length === 0 && (
        <div className={classes.historyItem}>
          <div className={classes.rewardRow}>
            <span className={classes.rewardName}>まだポイントを交換していません。</span>
          </div>
        </div>
      )}
      {histories.map((history: UserPointHistory) => (
        <div key={history.id} className={classes.historyItem}>
          <div className={classes.timestamp}>
            {new Date(history.createdAt).toLocaleString("ja-JP")}
          </div>
          <div className={classes.rewardRow}>
            <span className={classes.rewardName}>{history.giftName}</span>
            <span className={classes.point}>
            {history.pointChange.toLocaleString("ja-JP")}pt
            </span>
          </div>
        </div>
      ))}
      {histories.length !== 0 && (
        <div className={classes.historyItem}>
          <div className={classes.rewardSummaryRow}>
            <span className={classes.rewardName}>
              全{histories.length}件
            </span>
            <span className={classes.point}>
              {histories
                .reduce((sum, h) => sum + h.pointChange, 0)
                .toLocaleString("ja-JP")}
              pt
            </span>
          </div>
        </div>
      )}
    </div>
  );

  return (
      <div className={classes.container}>
        <Header />
        <TopHeader currentMenuUrl="/point" />
        <PointHeader aquiredPoint={currentPoint} />
        <div className={classes.comment}>
          <TeacherComment
            image={rabbitNormal}
            bgWhite={true}
            comment={[
              "みらいいポイントはギフトに交換できます！自分へのちょっとした「ごほうび」として楽しんでください♪",
            ]}
          />
        </div>
        <div className={classes.button}>
            <SimpleButton
              label="もっと詳しく"
              width="90%"
              color="orange"
              isRadius={true}
              onClick={() => navigate("/point-detail")}
            />
        </div>
        <div className={classes.pointHistoryBlock}>
          {pointSummaryOwners.map((owner) => {
            const {
              availableGiftPoint,
              increasedPoint,
              supportedPoint,
              totalPoint,
            } = owner;
            const exchangeHistories = getExchangeHistories(owner);

            return (
              <section key={owner.pointOwnerId} className={classes.ownerPointSection}>
                <div className={classes.historyHeader}>
                  <div className={classes.ownerLabel}>{getOwnerLabel(owner)}</div>
                  <div className={classes.totalPointTitle}>
                    <SparkleIcon />
                    <span>全保有ポイント</span>
                    <SparkleIcon />
                  </div>
                  <div className={classes.totalPointAmount}>
                    <span className={classes.amount}>{totalPoint.toLocaleString("ja-JP")}</span>
                    <span className={classes.unit}>pt</span>
                  </div>
                  <div className={classes.breakdownTitle}>-内訳-</div>
                  <div className={classes.pointBreakdown}>
                    <div className={classes.breakdownRow}>
                      <span>運用しているポイント</span>
                      <span>{supportedPoint.toLocaleString("ja-JP")}pt</span>
                    </div>
                    <div className={`${classes.breakdownRow} ${classes.accent}`}>
                      <span>余っているポイント</span>
                      <span>{availableGiftPoint.toLocaleString("ja-JP")}pt</span>
                    </div>
                    <div className={classes.breakdownRow}>
                      <span>運用して増えたポイント</span>
                      <span>{increasedPoint.toLocaleString("ja-JP")}pt</span>
                    </div>
                  </div>
                  <div className={classes.downArrow}> ⌄ </div>
                  <div className={classes.exchangePointLabel}>
                    ごほうびに交換できるポイント
                    <br />
                    <span>（余っているポイント）</span>
                  </div>
                  <div className={classes.exchangePointAmount}>
                    <span>{availableGiftPoint.toLocaleString("ja-JP")}</span>
                    <span>pt</span>
                  </div>
                  <div className={classes.exchangeButton}>
                    <SimpleButton
                      label="ポイントをごほうびに交換する"
                      width="90%"
                      color="orange"
                      isRadius={true}
                      onClick={() =>
                        navigate("/point-gift", {
                          state: {
                            ownerLabel: getOwnerLabel(owner),
                            pointOwnerId: owner.pointOwnerId,
                            availableGiftPoint,
                          },
                        })
                      }
                    />
                  </div>
                </div>
                {renderExchangeHistory(exchangeHistories)}
              </section>
            );
          })}
        </div>
          <BreadCrumb
            data={[
              { displayName: "みらいいコンシェル"},
              { displayName: "ポイント", url: "/point" }
            ]}
          />
      </div>
  );
};

export default Point;
